/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { decLoading, incLoading } from '@app/core/ngrx/actions/loading.actions';
import { Observable, merge } from 'rxjs';
import { debounceTime, filter, share, tap } from 'rxjs/operators';
import { simpleHook } from './simple-hook';

export const interceptActionTaskErrors = <T extends Record<K, (...args: any) => Observable<any>>, K extends keyof T>(
  target: T,
  methodName: K,
): Observable<unknown> => {
  return new Observable((observer) => {
    simpleHook(target, methodName, (response) =>
      response.pipe(tap({ error: (error: unknown) => observer.next(error) })),
    );
    return () => observer.complete();
  });
};

export const endpointLoading = <T extends Record<K, (...args: any) => Observable<any>>, K extends keyof T>(
  target: T,
  methodName: K,
  force?: boolean,
): Observable<ReturnType<typeof decLoading | typeof incLoading>> => {
  const key = String(methodName);
  const actions = new Observable<ReturnType<typeof decLoading | typeof incLoading>>((observer) => {
    const inc = () => observer.next(incLoading({ key }));
    const dec = () => {
      observer.next(force ? decLoading({ key, force }) : decLoading({ key }));
    };
    simpleHook(target, methodName, (response) => {
      inc();
      return response.pipe(tap({ error: dec, complete: dec }));
    });
    return () => observer.complete();
  }).pipe(share());
  // This is the same debounce strategy as in handleLoading operator.
  const incs = actions.pipe(
    debounceTime(500),
    filter((a): a is ReturnType<typeof incLoading> => a.type === incLoading.type),
  );
  const decs = actions.pipe(filter((a): a is ReturnType<typeof decLoading> => a.type === decLoading.type));
  return merge(incs, decs);
};
